/**
 * QuotesContracts Page
 * ====================
 * Displays completed RFQs/Quotes linked to invoices.
 * Reverse view of InvoiceReconciliation - shows quotes with their linked invoices.
 */

import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuotesData, Quote } from './hooks/useQuotesData';
import DataDashboard, { Column } from '../../components/common/DataDashboard';
import './QuotesContracts.scss';

// Currency normalization (TL -> TRY)
const CURRENCY_MAP: Record<string, string> = { 'TL': 'TRY', '₺': 'TRY', '$': 'USD', '€': 'EUR', '£': 'GBP' };
const normalizeCurrency = (c?: string) => c ? (CURRENCY_MAP[c] || CURRENCY_MAP[c.toUpperCase()] || c.toUpperCase()) : 'TRY';

// Utility to format currency
const formatCurrency = (amount?: number, currency = 'TRY') => {
    if (amount === undefined || amount === null) return '-';
    const code = normalizeCurrency(currency);
    try {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: code }).format(amount);
    } catch {
        return `${amount.toLocaleString('tr-TR')} ${code}`;
    }
};

// Utility to format date
const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return dateStr;
    }
};

const QuotesContracts: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const filterParam = searchParams.get('filter'); // 'matched' or null

    const { data, isLoading, error, pagination, fetchData } = useQuotesData();

    // Flatten nested data for sorting/filtering
    const flattenedData = useMemo(() => {
        return data.map(quote => ({
            ...quote,
            // Flatten for column key matching
            link_status: quote.has_invoice || (quote.linked_invoice_count && quote.linked_invoice_count > 0) ? 'LINKED' : 'UNLINKED',
            quote_details: quote.title || 'Untitled RFQ',
            supplier: quote.selected_supplier?.name || 'No Supplier Selected',
            amount: quote.final_amount || 0,
            linked_invoices: quote.linked_invoice_count || 0
        }));
    }, [data]);

    // Filter data based on URL parameter
    const filteredData = useMemo(() => {
        if (filterParam === 'matched') {
            return flattenedData.filter(quote =>
                quote.has_invoice ||
                (quote.linked_invoice_count && quote.linked_invoice_count > 0)
            );
        }
        return flattenedData;
    }, [flattenedData, filterParam]);

    // Columns Definition
    const columns: Column[] = [
        {
            key: 'link_status',
            title: 'Link Status',
            type: 'status',
            width: '120px',
            sortable: true,
            filterable: true,
            render: (_, row: Quote) => {
                const hasInvoice = row.has_invoice || (row.linked_invoice_count && row.linked_invoice_count > 0);
                return (
                    <span className={`badge ${hasInvoice ? 'linked' : 'unlinked'}`}>
                        {hasInvoice ? 'LINKED' : 'UNLINKED'}
                    </span>
                );
            }
        },
        {
            key: 'quote_details',
            title: 'Quote Details',
            type: 'text',
            sortable: true,
            filterable: true,
            render: (_, row: Quote) => (
                <div className="cell-stacked">
                    <span className="primary-value">{row.title}</span>
                    <span className="secondary-value">{formatDate(row.completed_at)}</span>
                </div>
            )
        },
        {
            key: 'supplier',
            title: 'Selected Supplier',
            type: 'text',
            sortable: true,
            filterable: true,
            render: (_, row: Quote) => (
                <div className="cell-stacked">
                    <span className="primary-value">
                        {row.selected_supplier?.name || 'No Supplier Selected'}
                    </span>
                    <span className="secondary-value">
                        {row.selected_supplier?.region || '-'}
                    </span>
                </div>
            )
        },
        {
            key: 'amount',
            title: 'Final Amount',
            type: 'number',
            sortable: true,
            filterable: true,
            render: (_, row: Quote) => (
                <div className="cell-stacked">
                    <span className="primary-value">
                        {formatCurrency(row.final_amount, row.currency)}
                    </span>
                    <span className="secondary-value">
                        {row.offer_count} offer{row.offer_count !== 1 ? 's' : ''}
                    </span>
                </div>
            )
        },
        {
            key: 'linked_invoices',
            title: 'Linked Invoices',
            type: 'text',
            width: '140px',
            sortable: true,
            filterable: true,
            render: (_, row: Quote) => {
                const count = row.linked_invoice_count || 0;
                return (
                    <div className={`invoice-count ${count > 0 ? 'has-invoices' : 'no-invoices'}`}>
                        {count} invoice{count !== 1 ? 's' : ''}
                    </div>
                );
            }
        },
        {
            key: 'actions',
            title: 'Actions',
            type: 'action',
            width: '120px',
            sortable: false,
            filterable: false,
            render: (_, row: Quote) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        className="action-link-btn"
                        onClick={() => navigate(`/reconciliation/${row.id}`)}
                        title="View Details"
                    >
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="quotes-contracts">
            <DataDashboard
                title="Quotes & Contracts"
                collection="quotes"
                data={filteredData}
                columns={columns}
                isLoading={isLoading}
                error={error}
                paginationInfo={pagination}
                showAddButton={false}
                onDataFetch={fetchData}
                pageDescription="View completed RFQs and their linked invoices"
            />
        </div>
    );
};

export default QuotesContracts;
